import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { Button } from "@/modules/shared/ui/components/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/modules/shared/ui/components/card"
import { Input } from "@/modules/shared/ui/components/input"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/modules/shared/ui/components/form"
import { FaEye, FaEyeSlash } from "react-icons/fa"
import { apiClient as api } from "@/lib/api-client"
import { Link, useNavigate } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { GoogleAuthButton } from "@/modules/auth/shared/components/GoogleAuthButton"
import { FetchCurrentUserAsync } from "@/modules/auth/infrastructure/auth-service"
import {
  logout as logoutAction,
  restoreAuth,
} from "@/modules/auth/store/authSlice"
import { useDispatch } from "react-redux"
import { AppRole } from "@/modules/auth/constants/app-role"
import { broadcastLogin } from "@/lib/session-sync"
import { clearAllHubConnections } from "@/modules/recruitment/applications/infrastructure/signalr-service"
import { toast } from "sonner"
import { AxiosError } from "axios"

const formSchema = z.object({
  userName: z.string().min(1, {
    message: "Username or email is required.",
  }),
  password: z.string().min(1, {
    message: "Password is required.",
  }),
})

type FormValues = z.infer<typeof formSchema>

interface LoginFormProps {
  onSubmit: (userName: string, password: string) => Promise<void>
  onMicrosoftLogin: () => void
  isLoading?: boolean
  isCoolingDown?: boolean
}

export default function LoginForm({
  onSubmit,
  onMicrosoftLogin: _onMicrosoftLogin,
  isLoading = false,
  isCoolingDown = false,
}: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false)
  const [formError, setFormError] = useState("")

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      userName: "",
      password: "",
    },
  })

  const handleSubmit = async (values: FormValues) => {
    setFormError("")

    try {
      await onSubmit(values.userName, values.password)
      // Navigation is handled by the parent (LoginPage) after successful login
    } catch (err) {
      const message =
        (err as Error)?.message ||
        "Login failed. Please check your credentials."
      setFormError(message)
    }
  }

  const isBusy = form.formState.isSubmitting || isLoading || isCoolingDown

  return (
    <div className="w-full md:w-[75%] justify-center items-center">
      <Card className="shadow-none border-0 w-full">
        <CardHeader>
          <CardTitle className="text-3xl">Log in to your account</CardTitle>
          <CardDescription className="text-base text-muted-foreground">
            Log in to your account to manage employees
          </CardDescription>
        </CardHeader>

        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-5"
            >
              <FormField
                control={form.control}
                name="userName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground font-medium">
                      Username or Email
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter your username or email"
                        className="border-border focus:border-blue-500 focus:ring-blue-500"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel className="text-foreground font-medium">
                        Password
                      </FormLabel>
                      <Link
                        to="/forgot-password"
                        className="text-sm font-medium hover:underline"
                        style={{ color: "#1A237E" }}
                      >
                        Forgot your password?
                      </Link>
                    </div>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter password"
                          className="pr-10 border-border focus:border-blue-500 focus:ring-blue-500"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((prev) => !prev)}
                          className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {formError && (
                <p className="text-sm text-red-600 mt-1">{formError}</p>
              )}

              <Button
                id="login-button"
                type="submit"
                className="w-full text-white py-6 text-base"
                style={{ backgroundColor: "#1A237E" }}
                disabled={isBusy}
              >
                {isCoolingDown
                  ? "Try again later"
                  : form.formState.isSubmitting || isLoading
                    ? "Logging in..."
                    : "Log in to dashboard"}
              </Button>
            </form>
          </Form>
        </CardContent>

        <CardFooter className="flex flex-col gap-4">
          <div className="text-center text-muted-foreground text-sm w-full">
            Or continue with
          </div>

          <div className="flex justify-center gap-3 w-full">
            {/* Google placeholder */}
            <div className="w-12 h-12 flex items-center justify-center rounded-full overflow-hidden">
              <GoogleAuthButton
                onSuccess={async (idToken: string) => {
                  try {
                    queryClient.clear()
                    clearAllHubConnections()
                    sessionStorage.clear()
                    dispatch(logoutAction())

                    await api.post("/auth/external-login", { token: idToken })

                    const userProfile = await FetchCurrentUserAsync()

                    dispatch(
                      restoreAuth({
                        user: {
                          user: userProfile,
                          roles: userProfile.roles,
                          permissions: userProfile.permissions,
                          currentOrganizationId:
                            userProfile.currentOrganizationId || null,
                        },
                        loginTimestamp: Date.now(),
                      }),
                    )
                    broadcastLogin(userProfile.email ?? "")

                    if (userProfile.needsLegalConsent) {
                      navigate("/accept-legal-documents", { replace: true })
                      return
                    }
                    const isEmployee = userProfile.roles?.includes(
                      AppRole.Employee,
                    )
                    navigate(
                      isEmployee ? "/employee/dashboard" : "/dashboard",
                      { replace: true },
                    )
                  } catch (err: unknown) {
                    const axiosErr = err as AxiosError<{ message?: string }>
                    const message =
                      axiosErr?.response?.data?.message ??
                      "Google sign-in failed. Please try again."
                    toast.error(message)
                  }
                }}
              />
            </div>
          </div>

          <p className="text-center text-muted-foreground text-sm w-full">
            Don&apos;t have an account?{" "}
            <Link
              to="/signup"
              type="button"
              className="font-medium text-[#40C4FF]"
            >
              Sign up
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  )
}
